/**
 * Event Planner Quiz — Multi-step lead magnet quiz
 *
 * 6-step wizard capturing event type, use cases, budget,
 * event details, role/industry, and contact info. Sends data to Klaviyo via
 * _learnq.push() and shows scored product recommendations
 * using Liquid-rendered product cards.
 */

(function() {
  'use strict';

  var TOTAL_STEPS = 6;
  var KLAVIYO_TIMEOUT_MS = 5000;
  var SUBMISSION_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24h
  var AUTO_ADVANCE_DELAY = 600; // ms after selection
  var OTHER_DEBOUNCE_DELAY = 1500; // ms after last keystroke

  var currentStep = 1;
  var answers = {
    eventType: '',
    otherEventText: '',
    useCases: [],
    budget: '',
    guestCount: '',
    eventVision: '',
    details: '',
    eventDate: '',
    role: '',
    name: '',
    email: ''
  };

  // Event plan content keyed by event type
  var PLANS = {
    graduation: {
      title: 'Your Graduation Celebration Plan',
      intro: "Celebrate this milestone with signage that shows how proud you are!",
      tip: 'Graduation season fills up fast — order 3-4 weeks early for the best selection.',
      boardSlug: 'graduation'
    },
    holiday: {
      title: 'Your Holiday Party Plan',
      intro: 'Make your holiday gathering extra festive with custom signage!',
      tip: 'Holiday orders spike in November — order early to avoid the rush!',
      boardSlug: 'holiday-party'
    },
    corporate: {
      title: 'Your Corporate Event Plan',
      intro: 'Professional signage that elevates your brand presence at any event.',
      tip: 'Need bulk pricing? Contact us for custom quotes on large orders.',
      boardSlug: 'corporate-events'
    },
    birthday: {
      title: 'Your Birthday Party Plan',
      intro: "Let's make this birthday one to remember with custom signs and decor!",
      tip: 'Order 2-3 weeks ahead for standard shipping, or rush options are available!',
      boardSlug: 'birthday-party'
    },
    church: {
      title: 'Your Church & Ministry Event Plan',
      intro: 'Meaningful signage for your congregation and ministry events.',
      tip: 'Many churches order for VBS, retreats, and special Sundays — plan ahead!',
      boardSlug: 'church-signs'
    },
    'baby-shower': {
      title: 'Your Baby Shower Plan',
      intro: "Sweet signage to celebrate the little one on the way!",
      tip: 'Planning a gender reveal? Our custom signs can keep the secret until the big moment!',
      boardSlug: 'baby-shower'
    },
    wedding: {
      title: 'Your Wedding Signage Plan',
      intro: "Congratulations! Here's a curated selection to make your big day unforgettable.",
      tip: 'Most couples order 4-6 weeks before the event for peace of mind.',
      boardSlug: 'wedding-signs'
    },
    other: {
      title: 'Your Custom Event Plan',
      intro: "Whatever you're celebrating, we've got the perfect signage for it!",
      tip: 'Not sure what you need? Browse our inspiration feed above for ideas!',
      boardSlug: null
    }
  };

  // Product relevance tags for scoring recommendations
  // events: first entry = primary (+5), rest = secondary (+3)
  // useCases: +2 per match
  var PRODUCT_TAGS = {
    'custom-popsigns':                       { events: ['wedding','corporate','graduation','birthday','church','baby-shower','holiday','other'], useCases: ['welcome-greeting','directional','table-decor','photo-booth'] },
    'classic-popsigns':                      { events: ['birthday','graduation','baby-shower','holiday','other'], useCases: ['welcome-greeting','party-props','photo-booth'] },
    'custom-fabric-pillowcase-banner':       { events: ['corporate','graduation','birthday','church'], useCases: ['stage-backdrop','outdoor-flags'] },
    'custom-fabric-pillowcase-backdrop':     { events: ['wedding','corporate','birthday','baby-shower'], useCases: ['stage-backdrop','photo-booth'] },
    '8x8-photo-backdrop-kit':               { events: ['wedding','birthday','baby-shower','corporate'], useCases: ['photo-booth','stage-backdrop'] },
    'lumina-letters':                        { events: ['wedding','birthday','baby-shower','corporate','graduation'], useCases: ['stage-backdrop','welcome-greeting','table-decor'] },
    'monument-3d-freestanding-foam-letters': { events: ['wedding','birthday','graduation','corporate'], useCases: ['stage-backdrop','welcome-greeting','photo-booth'] },
    'custom-premium-feather-flags':          { events: ['corporate','church','graduation','holiday'], useCases: ['outdoor-flags','directional'] },
    'classic-welcome-premium-feather-flags': { events: ['church','corporate','wedding'], useCases: ['outdoor-flags','welcome-greeting'] },
    'congrats-grad':                         { events: ['graduation'], useCases: ['welcome-greeting','party-props','photo-booth'] },
    'graduation-celebration':                { events: ['graduation'], useCases: ['party-props','photo-booth','stage-backdrop'] },
    'church-essentials':                     { events: ['church'], useCases: ['welcome-greeting','directional'] },
    'numbered-popsigns':                     { events: ['wedding','corporate','birthday'], useCases: ['table-decor','directional'] },
    'custom-canvas-tote-bag':               { events: ['corporate','wedding','church'], useCases: ['party-props'] },
    'custom-18-inch-foam-fingers':           { events: ['birthday','graduation','holiday'], useCases: ['party-props','photo-booth'] },
    'lumina-lightboxes':                     { events: ['wedding','corporate','baby-shower'], useCases: ['stage-backdrop','welcome-greeting'] },
    'custom-popup-x-stand-banner':           { events: ['corporate','church','graduation'], useCases: ['outdoor-flags','stage-backdrop','directional'] },
    'flower-seed-confetti-cannon':           { events: ['wedding','graduation','birthday','baby-shower'], useCases: ['party-props'] },
    'vote-for-the-dress':                    { events: ['wedding'], useCases: ['party-props','photo-booth'] }
  };

  var container, progressFill, stepIndicator;

  // ===== INIT =====

  function init() {
    container = document.querySelector('.event-planner-section');
    if (!container) return;

    progressFill = document.getElementById('epq-progress-fill');
    stepIndicator = document.getElementById('epq-step-indicator');

    // Check if already submitted recently
    var lastSubmit = localStorage.getItem('epq_last_submit');
    if (lastSubmit && (Date.now() - parseInt(lastSubmit, 10)) < SUBMISSION_COOLDOWN_MS) {
      showAlreadySubmitted();
      return;
    }

    bindCardSelections();
    bindCheckboxSelections();
    bindBudgetPills();
    bindRolePills();
    bindNavigation();
    bindSubmit();
  }

  // ===== PRODUCT SCORING =====

  function getRecommendedProducts(eventType, useCases) {
    var scores = {};

    Object.keys(PRODUCT_TAGS).forEach(function(handle) {
      var tag = PRODUCT_TAGS[handle];
      var score = 0;

      // Event type match: +5 if primary (first in list), +3 if secondary
      var eventIdx = tag.events.indexOf(eventType);
      if (eventIdx === 0) score += 5;
      else if (eventIdx > 0) score += 3;

      // Use case match: +2 per matching use case
      useCases.forEach(function(uc) {
        if (tag.useCases.indexOf(uc) >= 0) score += 2;
      });

      // Small boost for universal custom product
      if (handle === 'custom-popsigns' && score > 0) score += 1;

      if (score > 0) scores[handle] = score;
    });

    // Sort by score descending, return top 6
    return Object.keys(scores).sort(function(a, b) {
      return scores[b] - scores[a];
    }).slice(0, 6);
  }

  // ===== STEP NAVIGATION =====

  function goToStep(step) {
    if (step < 1 || step > TOTAL_STEPS) return;

    var outgoing = container.querySelector('.epq-step--active');
    var incoming = container.querySelector('[data-step="' + step + '"]');
    if (!incoming || incoming === outgoing) return;

    var direction = step > currentStep ? -1 : 1;

    if (outgoing) {
      outgoing.classList.remove('epq-step--active');
      outgoing.style.transform = 'translateX(' + (direction * 60) + 'px)';
      outgoing.style.opacity = '0';
      setTimeout(function() {
        outgoing.style.transform = '';
        outgoing.style.opacity = '';
      }, 300);
    }

    currentStep = step;

    incoming.classList.add('epq-step--active');
    incoming.style.transform = 'translateX(' + (-direction * 60) + 'px)';
    incoming.style.opacity = '0';
    requestAnimationFrame(function() {
      requestAnimationFrame(function() {
        incoming.style.transform = 'translateX(0)';
        incoming.style.opacity = '1';
      });
    });

    // Update progress
    if (progressFill) {
      progressFill.style.width = ((step / TOTAL_STEPS) * 100) + '%';
    }
    if (stepIndicator) {
      stepIndicator.textContent = 'Step ' + step + ' of ' + TOTAL_STEPS;
    }
  }

  function bindNavigation() {
    // Back buttons
    var backBtns = container.querySelectorAll('.epq-btn--back');
    backBtns.forEach(function(btn) {
      btn.addEventListener('click', function() {
        var target = parseInt(btn.dataset.go, 10);
        goToStep(target);
      });
    });

    // Step 4 Next (always enabled, optional step)
    var step4Next = container.querySelector('[data-step="4"] .epq-btn--next');
    if (step4Next) {
      step4Next.addEventListener('click', function() {
        answers.guestCount = (document.getElementById('epq-guests') || {}).value || '';
        answers.eventVision = (document.getElementById('epq-vision') || {}).value || '';
        answers.details = (document.getElementById('epq-details') || {}).value || '';
        answers.eventDate = (document.getElementById('epq-date') || {}).value || '';
        goToStep(5);
      });
    }
  }

  // ===== STEP 1: Card Selection (event type) — auto-advance =====

  function bindCardSelections() {
    var cards = container.querySelectorAll('.epq-card-option');
    var otherWrap = document.getElementById('epq-other-wrap');
    var otherInput = document.getElementById('epq-other-text');
    var autoAdvanceTimer = null;
    var otherDebounceTimer = null;

    cards.forEach(function(card) {
      card.addEventListener('click', function() {
        clearTimeout(autoAdvanceTimer);
        clearTimeout(otherDebounceTimer);

        // Deselect all, select this one
        cards.forEach(function(c) { c.classList.remove('epq-card-option--selected'); });
        card.classList.add('epq-card-option--selected');
        answers.eventType = card.dataset.value;

        if (card.dataset.value === 'other') {
          // Show inline text input
          if (otherWrap) otherWrap.style.display = 'block';
          if (otherInput) {
            otherInput.focus();
            otherInput.value = '';
          }
        } else {
          // Hide "other" input if visible
          if (otherWrap) otherWrap.style.display = 'none';
          answers.otherEventText = '';
          // Auto-advance after delay
          autoAdvanceTimer = setTimeout(function() {
            goToStep(2);
          }, AUTO_ADVANCE_DELAY);
        }
      });
    });

    // "Other" text input: debounce after last keystroke, or Enter key
    if (otherInput) {
      otherInput.addEventListener('input', function() {
        clearTimeout(otherDebounceTimer);
        answers.otherEventText = otherInput.value.trim();
        if (answers.otherEventText.length > 0) {
          otherDebounceTimer = setTimeout(function() {
            goToStep(2);
          }, OTHER_DEBOUNCE_DELAY);
        }
      });

      otherInput.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' && answers.otherEventText.length > 0) {
          e.preventDefault();
          clearTimeout(otherDebounceTimer);
          goToStep(2);
        }
      });
    }
  }

  // ===== STEP 2: Checkbox Selection (use cases) =====

  function bindCheckboxSelections() {
    var checkboxes = container.querySelectorAll('.epq-checkbox');
    var nextBtn = document.getElementById('epq-next-2');

    checkboxes.forEach(function(cb) {
      cb.addEventListener('change', function() {
        answers.useCases = [];
        checkboxes.forEach(function(c) {
          if (c.checked) answers.useCases.push(c.value);
        });
        if (nextBtn) nextBtn.disabled = answers.useCases.length === 0;
      });
    });

    if (nextBtn) {
      nextBtn.addEventListener('click', function() {
        if (answers.useCases.length > 0) goToStep(3);
      });
    }
  }

  // ===== STEP 3: Budget Pills — auto-advance =====

  function bindBudgetPills() {
    var step3 = container.querySelector('[data-step="3"]');
    if (!step3) return;
    var pills = step3.querySelectorAll('.epq-pill-option');
    var autoAdvanceTimer = null;

    pills.forEach(function(pill) {
      pill.addEventListener('click', function() {
        clearTimeout(autoAdvanceTimer);
        pills.forEach(function(p) { p.classList.remove('epq-pill-option--selected'); });
        pill.classList.add('epq-pill-option--selected');
        answers.budget = pill.dataset.value;
        autoAdvanceTimer = setTimeout(function() {
          goToStep(4);
        }, AUTO_ADVANCE_DELAY);
      });
    });
  }

  // ===== STEP 5: Role Pills — auto-advance =====

  function bindRolePills() {
    var step5 = container.querySelector('[data-step="5"]');
    if (!step5) return;
    var pills = step5.querySelectorAll('.epq-pill-option');
    var autoAdvanceTimer = null;

    pills.forEach(function(pill) {
      pill.addEventListener('click', function() {
        clearTimeout(autoAdvanceTimer);
        pills.forEach(function(p) { p.classList.remove('epq-pill-option--selected'); });
        pill.classList.add('epq-pill-option--selected');
        answers.role = pill.dataset.value;
        autoAdvanceTimer = setTimeout(function() {
          goToStep(6);
        }, AUTO_ADVANCE_DELAY);
      });
    });
  }

  // ===== STEP 6: Submit =====

  function bindSubmit() {
    var nameInput = document.getElementById('epq-name');
    var emailInput = document.getElementById('epq-email');
    var submitBtn = document.getElementById('epq-submit');

    function validateContact() {
      var name = (nameInput ? nameInput.value.trim() : '');
      var email = (emailInput ? emailInput.value.trim() : '');
      var valid = name.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (submitBtn) submitBtn.disabled = !valid;
    }

    if (nameInput) nameInput.addEventListener('input', validateContact);
    if (emailInput) emailInput.addEventListener('input', validateContact);

    if (submitBtn) {
      submitBtn.addEventListener('click', function() {
        // Honeypot check
        var hp = document.getElementById('epq-hp');
        if (hp && hp.value) return;

        answers.name = (nameInput ? nameInput.value.trim() : '');
        answers.email = (emailInput ? emailInput.value.trim() : '');

        if (!answers.email) return;

        submitBtn.disabled = true;
        submitBtn.textContent = 'Creating your plan...';

        pushToKlaviyo();
        localStorage.setItem('epq_last_submit', String(Date.now()));
        showResult();
      });
    }
  }

  // ===== KLAVIYO =====

  function pushToKlaviyo() {
    waitForKlaviyo().then(function(available) {
      if (!available) return;

      try {
        var nameParts = answers.name.split(' ');
        var eventTypeLabel = answers.eventType === 'other'
          ? ('Other: ' + answers.otherEventText)
          : answers.eventType;

        // Identify
        var identifyPayload = {
          '$email': answers.email,
          '$first_name': nameParts[0] || '',
          'Event Type': eventTypeLabel,
          'Budget Range': answers.budget,
          'Use Cases': answers.useCases.join(', '),
          'Guest Count': answers.guestCount,
          'Role': answers.role
        };
        if (nameParts.length > 1) {
          identifyPayload['$last_name'] = nameParts.slice(1).join(' ');
        }
        window._learnq.push(['identify', identifyPayload]);

        // Track event
        window._learnq.push(['track', 'Event Planner Quiz Completed', {
          eventType: answers.eventType,
          otherEventText: answers.otherEventText,
          useCases: answers.useCases,
          budget: answers.budget,
          guestCount: answers.guestCount,
          eventVision: answers.eventVision,
          eventDetails: answers.details,
          eventDate: answers.eventDate,
          role: answers.role,
          submittedAt: new Date().toISOString(),
          source: 'design-gallery-quiz'
        }]);
      } catch (e) {
        console.warn('[EventPlannerQuiz] Klaviyo push error:', e);
      }
    });
  }

  function waitForKlaviyo() {
    return new Promise(function(resolve) {
      if (window._learnq) { resolve(true); return; }

      var interval = setInterval(function() {
        if (window._learnq) {
          clearInterval(interval);
          resolve(true);
        }
      }, 200);

      setTimeout(function() {
        clearInterval(interval);
        resolve(!!window._learnq);
      }, KLAVIYO_TIMEOUT_MS);
    });
  }

  // ===== RESULT CARD =====

  function showResult() {
    var plan = PLANS[answers.eventType] || PLANS.other;

    // Hide all steps
    var steps = container.querySelectorAll('.epq-step');
    steps.forEach(function(s) {
      s.classList.remove('epq-step--active');
      s.style.display = 'none';
    });

    // Hide progress
    var progress = container.querySelector('.epq-progress');
    if (progress) progress.style.display = 'none';
    if (stepIndicator) stepIndicator.style.display = 'none';

    // Show result
    var resultEl = container.querySelector('[data-step="result"]');
    if (!resultEl) return;

    resultEl.style.display = '';
    resultEl.classList.add('epq-step--active');

    var titleEl = document.getElementById('epq-result-title');
    var introEl = document.getElementById('epq-result-intro');
    var productsEl = document.getElementById('epq-result-products');
    var tipEl = document.getElementById('epq-result-tip');
    var inspoBtn = document.getElementById('epq-browse-inspo');

    // Personalize title for "other" with custom text
    if (titleEl) {
      if (answers.eventType === 'other' && answers.otherEventText) {
        titleEl.textContent = 'Your ' + capitalize(answers.otherEventText) + ' Plan';
      } else {
        titleEl.textContent = plan.title;
      }
    }
    if (introEl) introEl.textContent = plan.intro;

    // Show/hide Liquid-rendered product cards based on scoring
    if (productsEl) {
      var allCards = productsEl.querySelectorAll('.epq-product-card--real');
      allCards.forEach(function(card) { card.style.display = 'none'; });

      var recommended = getRecommendedProducts(answers.eventType, answers.useCases);
      recommended.forEach(function(handle) {
        var card = productsEl.querySelector('[data-product-handle="' + handle + '"]');
        if (card) card.style.display = '';
      });
    }

    if (tipEl) {
      tipEl.innerHTML = '<strong>Tip:</strong> ' + escapeHtml(plan.tip);
    }

    if (inspoBtn && plan.boardSlug) {
      inspoBtn.style.display = '';
      inspoBtn.textContent = 'Browse ' + capitalize(answers.eventType.replace(/-/g, ' ')) + ' Inspiration';
      inspoBtn.addEventListener('click', function() {
        if (window.inspoFeedFilterTo) {
          window.inspoFeedFilterTo(plan.boardSlug);
        }
      });
    }
  }

  function showAlreadySubmitted() {
    var steps = container.querySelectorAll('.epq-step');
    steps.forEach(function(s) { s.style.display = 'none'; });

    var progress = container.querySelector('.epq-progress');
    if (progress) progress.style.display = 'none';
    if (stepIndicator) stepIndicator.style.display = 'none';

    var card = container.querySelector('.epq-card');
    if (card) {
      var msg = document.createElement('div');
      msg.className = 'epq-already-submitted';
      msg.innerHTML =
        '<div class="epq-result-icon">✅</div>' +
        '<h3>You\'ve already submitted your event plan request!</h3>' +
        '<p>Check your inbox for your personalized event plan. Want to start over?</p>' +
        '<button type="button" class="epq-btn epq-btn--next" id="epq-reset">Start Over</button>';
      card.appendChild(msg);

      document.getElementById('epq-reset').addEventListener('click', function() {
        localStorage.removeItem('epq_last_submit');
        location.reload();
      });
    }
  }

  // ===== UTILITIES =====

  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function capitalize(str) {
    return str.replace(/\b\w/g, function(c) { return c.toUpperCase(); });
  }

  // ===== BOOT =====

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
